import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField as MuiTextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { MdSync } from '@teamworksdev/icons';

import { PageHeader } from '../../../components/layout/page-header';
import { useTumSync, type TumUser } from './useTumSync';

type OrgOption = { id: string; name: string };

/**
 * The "All teams" pseudo-option: its `id` is `null`, which is also what the
 * backend expects as `teamId` for an org-wide sync. `undefined` (nothing
 * chosen yet) never appears as an option value — an empty select is just no
 * selection.
 */
type TeamOption = { id: string | null; name: string };
const ALL_TEAMS_OPTION: TeamOption = { id: null, name: 'All teams' };

function accessLabel(value: boolean | null): 'enabled' | 'disabled' {
  return value === true ? 'enabled' : 'disabled';
}

export function formatAccessTransition(
  current: boolean | null,
  next: boolean,
): string {
  const currentLabel = accessLabel(current);
  const nextLabel = accessLabel(next);
  if (currentLabel === nextLabel) return currentLabel;
  return `${currentLabel} → ${nextLabel}`;
}

function userTypeLabel(isCoach: boolean): 'Coach' | 'Athlete' {
  return isCoach ? 'Coach' : 'Athlete';
}

export function formatUserTypeTransition(
  currentIsCoach: boolean,
  newIsCoach: boolean,
): string {
  const currentLabel = userTypeLabel(currentIsCoach);
  const nextLabel = userTypeLabel(newIsCoach);
  if (currentLabel === nextLabel) return currentLabel;
  return `${currentLabel} → ${nextLabel}`;
}

export function formatTeamsTransition(
  current: string[],
  next: string[],
): string {
  const currentLabel = current.length > 0 ? current.join(', ') : 'none';
  const nextLabel = next.length > 0 ? next.join(', ') : 'none';
  if (currentLabel === nextLabel) return currentLabel;
  return `${currentLabel} → ${nextLabel}`;
}

function UserChangesTable({ users }: { users: TumUser[] }) {
  return (
    <TableContainer sx={{ maxHeight: 500, overflow: 'auto' }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell>First Name</TableCell>
            <TableCell>Last Name</TableCell>
            <TableCell>User Type</TableCell>
            <TableCell>Athlete Teams</TableCell>
            <TableCell>Coach Teams</TableCell>
            <TableCell>Web Access</TableCell>
            <TableCell>Mobile Access</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.axleProfileId} className="even:bg-gray-100">
              <TableCell>{user.givenName}</TableCell>
              <TableCell>{user.familyName}</TableCell>
              <TableCell>
                {formatUserTypeTransition(user.currentIsCoach, user.newIsCoach)}
              </TableCell>
              <TableCell>
                {formatTeamsTransition(
                  user.currentTeamNames,
                  user.newTeamNames,
                )}
              </TableCell>
              <TableCell>
                {formatTeamsTransition(
                  user.currentCoachTeamNames,
                  user.newCoachTeamNames,
                )}
              </TableCell>
              <TableCell>
                {formatAccessTransition(
                  user.currentHasWebAccess,
                  user.newHasWebAccess,
                )}
              </TableCell>
              <TableCell>
                {formatAccessTransition(
                  user.currentHasMobileAccess,
                  user.newHasMobileAccess,
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * TUM Sync admin tool page: a three-step wizard (select organization, pick a
 * team and load users, sync users).
 *
 * The team select defaults to the first team so admins resync one team at a
 * time; "All teams" remains available with a warning that the sync will
 * likely fail for large organizations.
 */
export function TumSyncPage() {
  const {
    selectedOrgId,
    selectedTeamId,
    users,
    tumEnabled,
    isCheckingTumEnabled,
    adminHasCoachProfile,
    isCheckingCoachProfile,
    count,
    hasLoaded,
    isLoading,
    isSyncing,
    synced,
    syncedCount,
    failureCount,
    loadError,
    orgsQuery,
    teamsQuery,
    handleOrgChange,
    handleTeamChange,
    handleLoadUsers,
    handleSync,
  } = useTumSync();

  const teamOptions: TeamOption[] = [
    ALL_TEAMS_OPTION,
    ...(teamsQuery.data ?? []).map(
      (team: { id: string; name: string }): TeamOption => ({
        id: team.id,
        name: team.name,
      }),
    ),
  ];
  const selectedTeamOption =
    teamOptions.find((team) => team.id === selectedTeamId) ?? null;
  const isAllTeams = selectedTeamId === null;

  return (
    <div className="flex flex-col h-full overflow-auto">
      <PageHeader title="TUM Sync" />
      <div className="flex flex-col gap-6 p-6">
        <Paper className="p-6">
          <Typography variant="h2" className="mb-4">
            1. Select Organization
          </Typography>
          <Autocomplete
            options={(orgsQuery.data ?? []) as OrgOption[]}
            getOptionLabel={(option: OrgOption) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={
              (orgsQuery.data as OrgOption[] | undefined)?.find(
                (org) => org.id === selectedOrgId,
              ) ?? null
            }
            onChange={(_, newValue: OrgOption | null) =>
              handleOrgChange(newValue?.id ?? null)
            }
            renderInput={(params) => (
              <MuiTextField
                {...params}
                label="Organization"
                placeholder="Select an organization"
              />
            )}
            loading={orgsQuery.isLoading}
            fullWidth
          />
        </Paper>

        {selectedOrgId && (
          <Paper className="p-6">
            <Typography variant="h2" className="mb-4">
              2. Load Users
            </Typography>
            <Box className="flex flex-col gap-4">
              {(isCheckingTumEnabled || isCheckingCoachProfile) && (
                <CircularProgress size={20} />
              )}

              {adminHasCoachProfile === false && (
                <Alert severity="warning">
                  You need a coach profile in this organization to run TUM sync.
                </Alert>
              )}

              {tumEnabled === false && (
                <Alert severity="warning">
                  TUM access rules are not enabled for this organization.
                </Alert>
              )}

              <Autocomplete
                options={teamOptions}
                getOptionLabel={(option: TeamOption) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={selectedTeamOption}
                onChange={(_, newValue: TeamOption | null) =>
                  handleTeamChange(newValue ? newValue.id : undefined)
                }
                renderInput={(params) => (
                  <MuiTextField
                    {...params}
                    label="Team"
                    placeholder="Select a team"
                  />
                )}
                loading={teamsQuery.isLoading}
                fullWidth
              />

              {selectedTeamId === undefined && (
                <Alert severity="info">
                  Select a team to sync. Syncing a single team at a time is
                  faster and more reliable than syncing every team at once.
                </Alert>
              )}

              {isAllTeams && (
                <Alert severity="warning">
                  Syncing all teams will likely fail for large organizations.
                  Select a single team if possible.
                </Alert>
              )}

              {tumEnabled === true && adminHasCoachProfile === true && (
                <>
                  <Box>
                    <Button
                      variant="contained"
                      onClick={handleLoadUsers}
                      disabled={isLoading || selectedTeamId === undefined}
                      startIcon={
                        isLoading ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : undefined
                      }
                    >
                      {isLoading ? 'Loading...' : 'Load Users'}
                    </Button>
                  </Box>

                  {loadError && <Alert severity="error">{loadError}</Alert>}

                  {hasLoaded && users.length === 0 && (
                    <Alert severity="success">
                      All users are already in sync with TUM rules.
                    </Alert>
                  )}

                  {users.length > 0 && (
                    <>
                      <Alert severity="info">
                        <strong>{users.length}</strong> user
                        {users.length !== 1 ? 's' : ''} will be updated by this
                        sync.
                      </Alert>
                      <UserChangesTable users={users} />
                    </>
                  )}
                </>
              )}
            </Box>
          </Paper>
        )}

        {tumEnabled && adminHasCoachProfile && count > 0 && (
          <Paper className="p-6">
            <Typography variant="h2" className="mb-4">
              3. Sync Users
            </Typography>
            <Box className="flex flex-col gap-4">
              <Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSync}
                  disabled={isSyncing || synced}
                  startIcon={
                    isSyncing ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <MdSync />
                    )
                  }
                >
                  {isSyncing
                    ? 'Syncing...'
                    : `Sync ${count} User${count !== 1 ? 's' : ''}`}
                </Button>
              </Box>

              {synced && syncedCount > 0 && (
                <Alert severity="success">
                  Successfully synced <strong>{syncedCount}</strong> user
                  {syncedCount !== 1 ? 's' : ''}.
                </Alert>
              )}

              {synced && failureCount > 0 && (
                <Alert severity="error">
                  Failed to sync <strong>{failureCount}</strong> user
                  {failureCount !== 1 ? 's' : ''}. Check server logs for
                  details.
                </Alert>
              )}
            </Box>
          </Paper>
        )}
      </div>
    </div>
  );
}
